import type { ComputedRef, InjectionKey, Ref } from 'vue'
import type {
  GetUserProfileResponse,
  GetGrantedDatabasesResponse
} from '../api-requests/user/UserRequests'
import type { Database } from '../Database'
import type {
  UserStateKeyActiveDb,
  UserStateValueMapActiveDb,
  UserStateKeyGlobal,
  UserStateValueMapGlobal
} from '../UserState'
import type {
  GetUserStateActiveDbResponseMap,
  GetUserStateGlobalResponseMap
} from '../api-requests/UserStateRequests'
import type { DateFormat } from '../DateFormat'
import type { FirstDayOfWeek } from '../FirstDayOfWeek'
import type { NumberFormat } from '../NumberFormat'
import type { SystemAccessCode, AgencyAccessCode } from '../AccessCode'

// ── User Preferences ──────────────────────────────────────────────

export interface UserPreferencesData {
  userId: string
  locale: string
  theme: 'light' | 'dark' | 'auto'
  modalType: 'drawer' | 'dialog'
  dateFormat: DateFormat
  dateFormatInferFirstPartAs: 'month' | 'day'
  weekNumberFormat: string
  firstDayOfWeek: FirstDayOfWeek
  numberFormat: NumberFormat
  disabledFeatureFlags: string[]
}

export interface UserPreferencesService {
  systemSettings: ComputedRef<{
    timezone: string
    locale: Intl.Locale
  }>
  updateUserPreferences(
    preferences: Omit<Partial<UserPreferencesData>, 'userId'>
  ): void
  getUserPreferences(): UserPreferencesData
  fetchUserPreferences(): Promise<void>
}

// ── User State ────────────────────────────────────────────────────

export interface UserStateActiveDbService {
  state: ComputedRef<{
    [K in UserStateKeyActiveDb]: UserStateValueMapActiveDb[K]
  }>
  getState<K extends UserStateKeyActiveDb>(
    key: K
  ): Promise<UserStateValueMapActiveDb[K]>
  getMultipleItems<const K extends readonly UserStateKeyActiveDb[]>(
    keys: K
  ): Promise<GetUserStateActiveDbResponseMap<K, UserStateValueMapActiveDb>>
  setItem<K extends UserStateKeyActiveDb>(
    key: K,
    value: UserStateValueMapActiveDb[K]
  ): Promise<void>
  removeItem<K extends UserStateKeyActiveDb>(key: K): Promise<void>
}

export interface UserStateGlobalService {
  state: ComputedRef<{
    [K in UserStateKeyGlobal]: UserStateValueMapGlobal[K]
  }>
  getState<K extends UserStateKeyGlobal>(
    key: K
  ): Promise<UserStateValueMapGlobal[K]>
  getMultipleItems<const K extends readonly UserStateKeyGlobal[]>(
    keys: K
  ): Promise<GetUserStateGlobalResponseMap<K, UserStateValueMapGlobal>>
  setItemGlobal<K extends UserStateKeyGlobal>(
    key: K,
    value: UserStateValueMapGlobal[K]
  ): Promise<void>
  removeItem<K extends UserStateKeyGlobal>(key: K): Promise<void>
}

// ── Cache Invalidation ────────────────────────────────────────────

export interface CacheInvalidationService {
  isCooldownActive: Ref<boolean>
  remainingCooldownSeconds: ComputedRef<number>
  initializeCooldown(): void
  startCooldown(): void
  startCountdown(): void
  invalidateCache(options?: {
    reloadPage?: boolean
    force?: boolean
    delay?: number
  }): Promise<unknown>
}

// ── Database Info ─────────────────────────────────────────────────

export interface DatabaseInfo {
  current: Database | undefined
  all: Database[]
  support: Database[]
  default: Database | undefined
}

// ── User Service (main injection contract) ────────────────────────

export interface UserService {
  id: ComputedRef<string>
  userProfile: ComputedRef<GetUserProfileResponse | undefined>
  isTenantAdmin: ComputedRef<boolean>
  isSupportUser: ComputedRef<boolean>
  isSupportSession: ComputedRef<boolean>
  supportSessionId: Ref<string | null>
  accessRoleIds: ComputedRef<string[]>

  hasPermission: (accessCode: SystemAccessCode) => boolean
  hasAgencyPermissions: (
    accessCode: AgencyAccessCode,
    agencyNodeId: string
  ) => boolean
  hasMultiDBAccess: ComputedRef<boolean>
  databases: ComputedRef<DatabaseInfo>

  switchDatabase(id: string): void

  cacheInvalidation: CacheInvalidationService
  invalidateCache: CacheInvalidationService['invalidateCache']
  isCacheCooldownActive: Ref<boolean>
  cacheCooldownRemainingSeconds: ComputedRef<number>

  userPreferences: UserPreferencesService

  userState: {
    global: UserStateGlobalService
    activeDb: UserStateActiveDbService
  }

  /** @internal Orchestration — loads the user profile from the backend. */
  _loadUserProfile(): Promise<void>
  /** @internal Orchestration — loads user preferences from the backend. */
  _loadUserPreferences(): Promise<void>
  /** @internal Orchestration — fetches granted databases. */
  _fetchGrantedDatabases(): Promise<GetGrantedDatabasesResponse>
  /** @internal Orchestration — loads access codes from the backend. */
  _loadAccessCodes(): Promise<void>
  /** @internal Orchestration — subscribes to access-control SignalR events. */
  _subscribeToAccessControlEvents(signalRContext: unknown): void
}

/** @deprecated Use `UserService` instead. */
export type UserServiceContext = UserService

export const userServiceKey = Symbol(
  'gatship-user-service'
) as InjectionKey<UserService>
